import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BASE_REPORT_URL_TOKEN, Quote, SearchCriteria } from '@raptor-fe/core';
import { ServiceUtils } from '../service-utils/service-utils';
import { LoadingHeaders } from '../web-loading-service/web-loading-headers';

@Injectable({
    providedIn: 'root',
})
export class QuoteApiService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);
    private readonly base_report_url = inject(BASE_REPORT_URL_TOKEN);
    private httpHeadersWithNoLoading = LoadingHeaders.httpHeadersWithNoLoading();

    /**
     * Requests server-side generation of the estimate PDF report and returns an expiring signed link.
     * Mirrors {@link InspectionApiService.getReport}: the report API renders and stores the PDF, then
     * returns its URL rather than the bytes, so the caller opens/navigates to the returned URL. Sends the
     * no-loading header so report generation does not raise the global loading overlay -- the caller drives
     * its own local button spinner (store.generatingReport) instead.
     */
    getReport(tenantId: string, branchId: string, id: string): Observable<{ url: string; expiresOn: string | null }> {
        return this.http.get<{ url: string; expiresOn: string | null }>(
            `${this.base_report_url}v1/tenant/${tenantId}/branch/${branchId}/report/quote/${id}`,
            { headers: this.httpHeadersWithNoLoading },
        );
    }

    delete(tenantId: string, branchId: string, quoteId: string): Observable<void> {
        return this.http.delete<void>(`${this.serviceUtils.getBaseUrl('quote', tenantId, branchId)}/${quoteId}`);
    }

    get(tenantId: string, branchId: string, quoteId: string): Observable<Quote> {
        return this.http.get<Quote>(`${this.serviceUtils.getBaseUrl('quote', tenantId, branchId)}/${quoteId}`);
    }

    getByCustomerId(tenantId: string, branchId: string, customerId: string): Observable<Quote[]> {
        return this.http.get<Quote[]>(`${this.serviceUtils.getBaseUrl('quote', tenantId, branchId)}/customer/${customerId}`);
    }

    save(tenantId: string, branchId: string, quote: Quote): Observable<Quote> {
        if (!quote.id || quote.id === null) return this.http.post<Quote>(`${this.serviceUtils.getBaseUrl('quote', tenantId, branchId)}`, quote);
        else return this.http.put<Quote>(`${this.serviceUtils.getBaseUrl('quote', tenantId, branchId)}`, quote);
    }

    search(tenantId: string, branchId: string, criteria: SearchCriteria, showLoading = true): Observable<Quote[]> {
        const url = `${this.serviceUtils.getBaseUrl('quote', tenantId, branchId)}/search`;
        return this.http.post<Quote[]>(url, criteria, {
            headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
        });
    }

    searchByKeyword(
        tenantId: string,
        branchId: string,
        keyword: string,
        pageNumber: number,
        pageSize: number,
        orderBy = 'createdDate desc',
    ): Observable<Quote[]> {
        const body = { pageSize, pageNumber, items: [], orderBy };
        let url = `${this.serviceUtils.getBaseUrl('quote', tenantId, branchId)}/search/keyword`;
        if (keyword && keyword.length > 0) url += `/${encodeURIComponent(keyword)}`;
        return this.http.post<Quote[]>(url, body);
    }
}
